import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  Input,
  InputGroup,
  InputLeftElement,
  useToast,
  VStack,
  HStack,
  Spinner,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useDisclosure,
  IconButton,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Portal,
} from "@chakra-ui/react";
import {
  SearchIcon,
  AddIcon,
  EditIcon,
  DeleteIcon,
  ChevronDownIcon,
} from "@chakra-ui/icons";
import Card from "components/Card/Card.js";
import { colorsService } from "api";

export default function Colors() {
  const [colors, setColors] = useState([]);
  const [filteredColors, setFilteredColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [combinedWithFilter, setCombinedWithFilter] = useState("");
  const [editingColor, setEditingColor] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  
  const [colorToDelete, setColorToDelete] = useState(null);
  
  const toast = useToast();
  const textColor = useColorModeValue("gray.700", "white");
  const bgColor = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    hash_code: "#000000",
    combined_with: "",
  });

  // Form validation
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchColors();
  }, []);

  useEffect(() => {
    if (Array.isArray(colors)) {
      filterColors();
    }
  }, [colors, searchTerm, combinedWithFilter]);

  const fetchColors = async () => {
    try {
      setLoading(true);
      const response = await colorsService.getColors();
      
      console.log('Colors API Response:', response);
      console.log('Response data type:', typeof response.data);
      console.log('Response data:', response.data);
      console.log('Response status:', response.status);
      console.log('Response message:', response.message);
      console.log('Response success:', response.success);
      console.log('Response.data.data:', response.data?.data);
      console.log('Response.data.status:', response.data?.status);
      console.log('Response.data.message:', response.data?.message);
      
      // Check if response has data and status indicates success
      if (response.success && response.data && (response.data.status === 1 || response.success)) {
        // The actual colors data is nested in response.data.data due to httpClient wrapper
        const colorsData = response.data.data || [];
        console.log('Colors data before setting:', colorsData);
        console.log('Is array?', Array.isArray(colorsData));
        setColors(Array.isArray(colorsData) ? colorsData : []);
      } else if (response.success && Array.isArray(response.data)) {
        // Fallback: if response.data is directly an array
        console.log('Direct array response detected');
        setColors(response.data);
      } else {
        setColors([]);
        toast({
          title: "خطأ",
          description: response.data?.message || response.message || "حدث خطأ أثناء جلب الألوان",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error fetching colors:", error);
      setColors([]);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء جلب الألوان",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filterColors = () => {
    if (!Array.isArray(colors)) {
      setFilteredColors([]);
      return;
    }
    
    let filtered = [...colors];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(color =>
        color.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        color.hash_code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply combined_with filter
    if (combinedWithFilter) {
      if (combinedWithFilter === "with_combination") {
        filtered = filtered.filter(color => color.combined_with !== null);
      } else if (combinedWithFilter === "without_combination") {
        filtered = filtered.filter(color => color.combined_with === null);
      } else {
        filtered = filtered.filter(color => 
          color.combined_with && color.combined_with.id.toString() === combinedWithFilter
        );
      }
    }

    setFilteredColors(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "اسم اللون مطلوب";
    }
    if (!formData.hash_code.trim()) {
      newErrors.hash_code = "رمز اللون مطلوب";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    
    try {
      let response;
      if (editingColor) {
        response = await colorsService.updateColor(editingColor.id, formData);
      } else {
        response = await colorsService.createColor(formData);
      }

      if (response.success) {
        toast({
          title: "نجح",
          description: editingColor 
            ? "تم تحديث اللون بنجاح" 
            : "تم إنشاء اللون بنجاح",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        
        onClose();
        resetForm();
        fetchColors();
      } else {
        toast({
          title: "خطأ",
          description: response.message || "حدث خطأ أثناء حفظ اللون",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error saving color:", error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء حفظ اللون",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (color) => {
    setEditingColor(color);
    setFormData({
      name: color.name,
      hash_code: color.hash_code,
      combined_with: color.combined_with ? color.combined_with.id.toString() : "",
    });
    setErrors({});
    onOpen();
  };

  const handleDelete = async () => {
    if (!colorToDelete) return;

    try {
      const response = await colorsService.deleteColor(colorToDelete.id);
      
      if (response.success) {
        toast({
          title: "نجح",
          description: "تم حذف اللون بنجاح",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        
        onDeleteClose();
        setColorToDelete(null);
        fetchColors();
      } else {
        toast({
          title: "خطأ",
          description: response.message || "حدث خطأ أثناء حذف اللون",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error deleting color:", error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء حذف اللون",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      hash_code: "#000000",
      combined_with: "",
    });
    setEditingColor(null);
    setErrors({});
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  const openDeleteDialog = (color) => {
    setColorToDelete(color);
    onDeleteOpen();
  };

  // Get unique base colors for combined_with filter
  const getBaseColors = () => {
    if (!Array.isArray(colors)) return [];
    return colors.filter(color => color.combined_with === null);
  };

  const ColorSwatch = ({ color }) => (
    <Box
      w="24px"
      h="24px"
      borderRadius="md"
      bg={color.hash_code}
      border="2px solid"
      borderColor={color.hash_code === "#FFFFFF" ? "gray.300" : "transparent"}
      display="inline-block"
      mr={2}
      verticalAlign="middle"
    />
  );

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card>
        <Flex direction="column" w="100%">
          <Flex
            direction={{ sm: "column", lg: "row" }}
            justify="space-between"
            align={{ sm: "flex-start", lg: "center" }}
            mb="20px"
            gap="20px"
          >
            <Text color={textColor} fontSize="xl" fontWeight="bold">
              إدارة الألوان
            </Text>
            <Button
              colorScheme="blue"
              leftIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                onOpen();
              }}
            >
              إضافة لون جديد
            </Button>
          </Flex>

          {/* Search and Filters */}
          <Flex
            direction={{ base: "column", lg: "row" }}
            gap="20px"
            mb="20px"
            align={{ base: "stretch", lg: "center" }}
          >
            <InputGroup maxW={{ base: "100%", lg: "300px" }}>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="البحث في الألوان..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            <Select
              maxW={{ base: "100%", lg: "200px" }}
              value={combinedWithFilter}
              onChange={(e) => setCombinedWithFilter(e.target.value)}
              placeholder="جميع الألوان"
            >
              <option value="">جميع الألوان</option>
              <option value="with_combination">ألوان مركبة</option>
              <option value="without_combination">ألوان أساسية</option>
              {Array.isArray(getBaseColors()) && getBaseColors().map(color => (
                <option key={color.id} value={color.id.toString()}>
                  {color.name}
                </option>
              ))}
            </Select>
          </Flex>

          {/* Colors Table */}
          {loading ? (
            <Flex justify="center" align="center" py="100px">
              <Spinner size="xl" color="blue.500" />
            </Flex>
          ) : (
            <TableContainer>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>اللون</Th>
                    <Th>الاسم</Th>
                    <Th>رمز اللون</Th>
                    <Th>مركب مع</Th>
                    <Th>تاريخ الإنشاء</Th>
                    <Th>الإجراءات</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {Array.isArray(filteredColors) && filteredColors.map((color) => (
                    <Tr key={color.id}>
                      <Td>
                        <ColorSwatch color={color} />
                      </Td>
                      <Td>
                        <Text color={textColor} fontWeight="500">
                          {color.name}
                        </Text>
                      </Td>
                      <Td>
                        <Flex align="center" gap={2}>
                          <Text fontFamily="mono">{color.hash_code}</Text>
                          <Box
                            w="16px"
                            h="16px"
                            borderRadius="sm"
                            bg={color.hash_code}
                            border="1px solid"
                            borderColor={color.hash_code === "#FFFFFF" ? "gray.300" : "transparent"}
                          />
                        </Flex>
                      </Td>
                      <Td>
                        {color.combined_with ? (
                          <Flex align="center" gap={2}>
                            <ColorSwatch color={color.combined_with} />
                            <Text>{color.combined_with.name}</Text>
                          </Flex>
                        ) : (
                          <Badge colorScheme="green">أساسي</Badge>
                        )}
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.500">
                          {new Date(color.created_at).toLocaleDateString('ar-SA')}
                        </Text>
                      </Td>
                      <Td>
                        <Flex gap={2}>
                          <Tooltip label="تعديل">
                            <IconButton
                              icon={<EditIcon />}
                              size="sm"
                              colorScheme="blue"
                              variant="ghost"
                              onClick={() => handleEdit(color)}
                            />
                          </Tooltip>
                          <Tooltip label="حذف">
                            <IconButton
                              icon={<DeleteIcon />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => openDeleteDialog(color)}
                            />
                          </Tooltip>
                        </Flex>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}

          {!loading && Array.isArray(filteredColors) && filteredColors.length === 0 && (
            <Flex justify="center" align="center" py="100px">
              <Text color="gray.500" fontSize="lg">
                لا توجد ألوان
              </Text>
            </Flex>
          )}
        </Flex>
      </Card>

      {/* Add/Edit Color Modal */}
      <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent mx={4}>
          <form onSubmit={handleSubmit}>
            <ModalHeader>
              {editingColor ? "تعديل اللون" : "إضافة لون جديد"}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isInvalid={!!errors.name}>
                  <FormLabel>اسم اللون</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="أدخل اسم اللون"
                  />
                  <FormErrorMessage>{errors.name}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.hash_code}>
                  <FormLabel>رمز اللون</FormLabel>
                  <Input
                    type="color"
                    value={formData.hash_code}
                    onChange={(e) => setFormData({ ...formData, hash_code: e.target.value })}
                  />
                  <FormErrorMessage>{errors.hash_code}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>مركب مع (اختياري)</FormLabel>
                  <Select
                    value={formData.combined_with}
                    onChange={(e) => setFormData({ ...formData, combined_with: e.target.value })}
                    placeholder="اختر لون أساسي للدمج"
                  >
                    {Array.isArray(getBaseColors()) && getBaseColors().map(color => (
                      <option key={color.id} value={color.id.toString()}>
                        {color.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={handleClose}>
                إلغاء
              </Button>
              <Button
                colorScheme="blue"
                type="submit"
                isLoading={isSubmitting}
                loadingText="جاري الحفظ..."
              >
                {editingColor ? "تحديث" : "إنشاء"}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={undefined}
        onClose={onDeleteClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              حذف اللون
            </AlertDialogHeader>

            <AlertDialogBody>
              هل أنت متأكد من حذف اللون "{colorToDelete?.name}"؟ لا يمكن التراجع عن هذا الإجراء.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button onClick={onDeleteClose}>
                إلغاء
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                حذف
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
